import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import AdminServices from "services/admin-services";
import { handlingUrlParams } from "Utils/AppUtils";
import * as Yup from "yup";

const validationSchema = Yup.object({
  name: Yup.string().trim().nullable(),
  documentReference: Yup.string().trim().nullable(),
  externalInvestorName: Yup.string().trim().nullable(),
  isBondInvestor: Yup.boolean().nullable(),
  pullCriteria: Yup.number().nullable(),
  createdFrom: Yup.date().nullable(),
  includeInactive: Yup.boolean().nullable(),
  createdTo: Yup.date()
    .nullable()
    .when("createdFrom", (createdFrom, schema) =>
      createdFrom
        ? schema.min(createdFrom, "To date cannot be before From date")
        : // eslint-disable-next-line prettier/prettier
          schema,
    ),
});

const InvestorFilters = ({
  onHideFilters,
  onApplyFilters,
  customerId,
  initialValues,
}: any) => {
  const [criteriaList, setCriteriaList] = useState([]);
  const bondInvestorOptions = [
    { label: "All", value: null },
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const [values, setValues] = useState({
    name: initialValues?.name || "",
    documentReference: initialValues?.documentReference || "",
    externalInvestorName: initialValues?.externalInvestorName || "",
    isBondInvestor: initialValues?.isBondInvestor ?? null,
    pullCriteria: initialValues?.pullCriteria ?? null,
    createdFrom: initialValues?.createdFrom || null,
    createdTo: initialValues?.createdTo || null,
    includeInactive: initialValues?.includeInactive ?? false,
  });

  const [errors, setErrors] = useState<any>({});

  const handleChange = (field: string, value: any) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = async () => {
    try {
      await validationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err: any) {
      const formattedErrors: any = {};
      err.inner.forEach((e: any) => {
        formattedErrors[e.path] = e.message;
      });
      setErrors(formattedErrors);
      return false;
    }
  };

  const handleApply = async () => {
    const isValid = await validate();
    if (!isValid) return;

    onApplyFilters(values);
    onHideFilters();
  };

  const getPullCriteria = (id: number) => {
    AdminServices.getPullCriteriaByID(handlingUrlParams({ customerId: id }))
      .then((res: any) => {
        const list =
          res?.data?.map((pkg: any) => ({
            label: pkg.name,
            value: pkg.id,
          })) || [];

        setCriteriaList(list);
      })
      .catch(console.error);
  };

  useEffect(() => {
    if (customerId) {
      getPullCriteria(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    setValues({
      name: initialValues?.name || "",
      documentReference: initialValues?.documentReference || "",
      externalInvestorName: initialValues?.externalInvestorName || "",
      isBondInvestor: initialValues?.isBondInvestor ?? null,
      pullCriteria: initialValues?.pullCriteria ?? null,
      createdFrom: initialValues?.createdFrom || null,
      createdTo: initialValues?.createdTo || null,
      includeInactive: initialValues?.includeInactive ?? false,
    });
  }, [initialValues]);

  const handleClear = () => {
    const clearedValues = {
      name: "",
      documentReference: "",
      externalInvestorName: "",
      isBondInvestor: null,
      pullCriteria: null,
      createdFrom: null,
      createdTo: null,
      includeInactive: false,
    };

    setValues(clearedValues);
    setErrors({});
    onApplyFilters(clearedValues);
    onHideFilters();
  };

  return (
    <div className="p-3">
      <h3 className="font-semibold text-xl my-2">Filters</h3>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <InputText
            value={values.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Search by Name"
            className="w-full"
          />
          {errors.name && <small className="p-error">{errors.name}</small>}
        </div>

        <div>
          <InputText
            value={values.documentReference}
            onChange={(e) => handleChange("documentReference", e.target.value)}
            placeholder="Search by Document Reference"
            className="w-full"
          />
          {errors.documentReference && (
            <small className="p-error">{errors.documentReference}</small>
          )}
        </div>

        <div>
          <InputText
            value={values.externalInvestorName}
            onChange={(e) =>
              handleChange("externalInvestorName", e.target.value)
            }
            placeholder="Search by External Investor Name"
            className="w-full"
          />
          {errors.externalInvestorName && (
            <small className="p-error">{errors.externalInvestorName}</small>
          )}
        </div>

        <div>
          <Dropdown
            value={values.isBondInvestor}
            options={bondInvestorOptions}
            onChange={(e) => handleChange("isBondInvestor", e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Bond Investor"
            className="w-full"
          />
          {errors.isBondInvestor && (
            <small className="p-error">{errors.isBondInvestor}</small>
          )}
        </div>

        <div>
          <Dropdown
            value={values.pullCriteria}
            options={criteriaList}
            onChange={(e) => handleChange("pullCriteria", e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Pull Criteria"
            className="w-full"
            filter
            showClear
          />
          {errors.pullCriteria && (
            <small className="p-error">{errors.pullCriteria}</small>
          )}
        </div>

        <div className="w-full">
          <label className="text-sm mb-1 block">Created Date Range</label>

          <div className="flex gap-4 items-center">
            <div className="w-1/2">
              <Calendar
                value={values.createdFrom}
                onChange={(e) => handleChange("createdFrom", e.value)}
                showIcon
                className="w-full"
                dateFormat="mm/dd/yy"
                placeholder="From"
              />
            </div>

            <div className="w-1/2">
              <Calendar
                value={values.createdTo}
                onChange={(e) => handleChange("createdTo", e.value)}
                showIcon
                className="w-full"
                dateFormat="mm/dd/yy"
                placeholder="To"
              />
            </div>
          </div>

          {errors.createdTo && (
            <small className="p-error">{errors.createdTo}</small>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            inputId="includeInactive"
            checked={values.includeInactive}
            onChange={(e) => handleChange("includeInactive", e.checked)}
          />
          <label htmlFor="includeInactive" className="text-sm">
            Include Inactive
          </label>
        </div>

        <div className="flex flex-row-reverse gap-4">
          <Button
            onClick={handleApply}
            label="Apply"
            className="p-button-sm p-button-outlined p-button-success"
          />
          <Button
            onClick={handleClear}
            label="Clear Filters"
            className="p-button-sm p-button-outlined p-button-warning"
          />
        </div>
      </div>
    </div>
  );
};

export default InvestorFilters;
